import {
  AccordionSummary,
  AccordionDetails,
  Accordion,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Typography,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
  Alert,
  IconButton,
  Tooltip,
  Chip,
  Switch,
  FormControlLabel,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { Box } from "@mui/system";
import React, { Fragment, useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { subMonths, format, addDays } from "date-fns";
import HttpService from "../services/HttpService";
import CircularProgress from "@mui/material/CircularProgress";
import CustomerActivityDialog from "./CustomerActivityDialog";
import PaidIcon from "@mui/icons-material/Paid";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import BusinessIcon from "@mui/icons-material/Business";
import BadgeIcon from "@mui/icons-material/Badge";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import FingerprintIcon from "@mui/icons-material/Fingerprint";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import MoneyOffIcon from "@mui/icons-material/MoneyOff";
import DownloadDoneIcon from "@mui/icons-material/DownloadDone";
import SavingsIcon from "@mui/icons-material/Savings";
import AlternateEmailIcon from "@mui/icons-material/AlternateEmail";
import PayAnyDayAdvances from "./PayAnyDayAdvances";
import PayAnyDayPaybacks from "./PayAnyDayPaybacks";
import QueryTimeAttendance from "../pages/QueryTimeAttendance";
import CardCredits from "./CardCredits";
import SendMoneyTransferList from "./SendMoneyTransferList";
import PayMeNowList from "./PayMeNowList";
import Labels from "../labels/Labels";
import TextSnippetIcon from "@mui/icons-material/TextSnippet";
import CloseIcon from "@mui/icons-material/Close";
import RequestQuoteIcon from "@mui/icons-material/RequestQuote";
import RenderOnRole from "./RenderOnRole";
import ForumIcon from "@mui/icons-material/Forum";
import SupportChatConversation from "../pages/crm/SupportChatConversation";

export default function MemberDetail(props) {
  // Format a value as currency (USD)
  function formatCurrency(value) {
    if (value === null || value === undefined || isNaN(value)) return "";
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(Number(value));
  }
  const [basicInfoData, setBasicInfoData] = useState({});
  const [padEnabled, setPadEnabled] = useState(true);
  const [ddLimitValidation, setDdLimitValidation] = useState(true);
  const [transferType, setTransferType] = useState("instant");
  const [dateRange] = useState(() => {
    const to = addDays(new Date(), 1);
    const from = subMonths(to, 3); // Last 3 months by default
    return {
      from: format(from, "dd/MM/yyyy"),
      to: format(to, "dd/MM/yyyy"),
    };
  });
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [feedback, setFeedback] = useState({
    open: false,
    type: "success",
    message: "",
  });
  const [eventsDialog, setEventsDialog] = useState({
    open: false,
    events: [],
    loading: false,
    title: "Card Validation Events",
    context: "",
  });
  const [activityDialog, setActivityDialog] = useState({
    open: false,
    activities: [],
    loading: false,
    fetchingData: false, // Separate state for the refresh icon
    title: "Customer Activity",
    context: "",
    totalCount: 0,
    page: 0,
    pageSize: 50,
    hasMore: false,
    filters: {}, // Add filters to state
  });
  const { memberInfo, companyView } = props;
  const queryClient = useQueryClient();
  async function fetchAdvancesDetail() {
    const response = await HttpService.getAxiosClient().get(
      window.API_URL +
        `/pad/advances?memberId=` +
        memberInfo.memberId +
        `&companyId=` +
        memberInfo.companyId
    );
    return response;
  }

  async function fetchPaybackDetail() {
    const response = await HttpService.getAxiosClient().get(
      window.API_URL +
        `/pad/payback?memberId=` +
        memberInfo.memberId +
        `&companyId=` +
        memberInfo.companyId
    );
    return response;
  }

  async function fetchCardCredits() {
    const response = await HttpService.getAxiosClient().get(
      window.API_URL +
        `/pad/credits?memberId=` +
        memberInfo.memberId +
        `&companyId=` +
        memberInfo.companyId
    );
    return response;
  }

  async function fetchBasicInfo() {
    const response = await HttpService.getAxiosClient().get(
      window.API_URL +
        `/pad/info?memberId=` +
        memberInfo.memberId +
        `&companyId=` +
        memberInfo.companyId
    );
    return response;
  }

  async function fetchPayMeNow() {
    const response = await HttpService.getAxiosClient().get(
      window.API_URL +
        `/member/action/pay-now?idEmployee=` +
        memberInfo.memberId +
        `&idEmployer=` +
        memberInfo.companyId
    );
    return response;
  }

  //https://rds-dev.rellevate.com/api/payment/transfer?from=11/07/2025&to=10/10/2025&type=scheduled&cursor=0
  async function fetchTransferAll(
    customerId,
    transferType,
    dateRange,
    cursor,
    pageSize
  ) {
    const response = await HttpService.getAxiosClient().get(
      window.API_URL +
        `/member/transfers?customerId=${customerId}&from=${dateRange.from}&to=${dateRange.to}&type=${transferType}&cursor=${cursor}&pageSize=${pageSize}`
    );
    return response;
  }

  const {
    data: dataAdvancesDetail,
    isError: isErrorAdvancesDetail,
    isFetching: isFetchingAdvancesDetail,
  } = useQuery(["advancesDetail"], fetchAdvancesDetail);

  const {
    data: dataPaybackDetail,
    isError: isErrorPaybackDetail,
    isFetching: isFetchingPaybackDetail,
  } = useQuery(["paybackDetail"], fetchPaybackDetail, {
    enabled: !companyView,
  });

  const {
    data: dataCardCredits,
    isError: isErrorCardCredits,
    isFetching: isFetchingCardCredits,
  } = useQuery(["cardCredits"], fetchCardCredits, {
    enabled: !companyView,
  });

  const {
    data: dataBasicInfo,
    isError: isErrorBasicInfo,
    isFetching: isFetchingBasicInfo,
    isLoading: isLoadingBasicInfo,
  } = useQuery(["basicInfo"], fetchBasicInfo);

  const [payMeNowExpanded, setPayMeNowExpanded] = useState(false);
  const [chatsExpanded, setChatsExpanded] = useState(false);
  const [chatConvId, setChatConvId] = useState(null);

  const {
    data: memberConversations,
    isFetching: isFetchingConversations,
    isError: isErrorConversations,
  } = useQuery(
    ["memberConversations", memberInfo?.idCustomer],
    async () => {
      const res = await HttpService.getAxiosClient().get(
        window.API_URL + "/chat/support/conversations/all"
      );
      return (res.data || []).filter(
        (c) => c.ownerId === String(memberInfo?.idCustomer)
      );
    },
    {
      enabled: chatsExpanded && !!memberInfo?.idCustomer,
      refetchOnWindowFocus: false,
    }
  );
  const {
    data: dataPayMeNow,
    isError: isErrorPayMeNow,
    isFetching: isFetchingPayMeNow,
    isLoading: isLoadingPayMeNow,
  } = useQuery(
    ["payMeNow", memberInfo.memberId, memberInfo.companyId],
    fetchPayMeNow,
    {
      enabled: payMeNowExpanded,
    }
  );

  // Function to fetch employer attributes
  async function fetchEmployerAttribute(attributeName, companyId) {
    try {
      const response = await HttpService.getAxiosClient().get(
        window.API_URL +
          `/employer/attributes/${attributeName}?employerId=${companyId}`,
        {
          // Add validateStatus to handle empty responses
          validateStatus: function (status) {
            return status >= 200 && status < 500; // Accept all 2xx-4xx status codes
          },
        }
      );

      // Backend returns null when attribute doesn't exist, which results in empty response body
      // Axios still returns 200 status with data: null or data: ""
      if (
        response.data === null ||
        response.data === "" ||
        response.data === undefined
      ) {
        return { data: null, status: 204 }; // Normalize to 204 No Content
      }

      return response;
    } catch (error) {
      // Catch network errors including ERR_EMPTY_RESPONSE
      // Handle ERR_EMPTY_RESPONSE as attribute not found
      if (
        error.code === "ERR_EMPTY_RESPONSE" ||
        error.message?.includes("ERR_EMPTY_RESPONSE")
      ) {
        return { data: null, status: 204 };
      }

      // Return normalized response for consistency
      return { data: null, status: error.response?.status || 500 };
    }
  }

  // Query to fetch employer attribute for PayMeNow feature
  const { data: isPayMeNowEnabled, isSuccess: isPayMeNowSuccess } = useQuery(
    ["payMeNowEnabled", memberInfo?.companyId],
    () => fetchEmployerAttribute("PAY_ME_NOW_ATTRIBUTE", memberInfo?.companyId),
    {
      enabled: !!memberInfo?.companyId,
      staleTime: 0,
      cacheTime: 0,
      select: (response) => {
        // If no response, 204 status, or no data/null data, feature is disabled
        if (
          !response ||
          response.status === 204 ||
          !response.data ||
          response.data === null ||
          response.data === ""
        ) {
          return false;
        }

        // Check both possible field names and also check the response.data directly if it's a string
        const attributeValue =
          response.data.value ||
          response.data.attributeValue ||
          (typeof response.data === "string" ? response.data : null);

        const isEnabled = attributeValue === "true";

        return isEnabled;
      },
    }
  );

  // Query to fetch employer attribute for disabling PAD features
  // SERIALIZED: Only fetch AFTER PayMeNow query completes to avoid race conditions
  const { data: isPadDisabled } = useQuery(
    ["padFeatureDisabled", memberInfo?.companyId], // Include companyId in the key
    () => fetchEmployerAttribute("FEATURE_DISABLED_PAD", memberInfo?.companyId),
    {
      enabled: !!memberInfo?.companyId && isPayMeNowSuccess, // ⚠️ WAIT for PayMeNow query to finish
      staleTime: 0, // Always fetch fresh data, no caching
      cacheTime: 0, // Don't cache the data at all
      select: (response) => {
        // If no response or 204 No Content, PAD is not disabled
        if (!response || response.status === 204 || !response.data) {
          return false;
        }

        // Check if the attribute value is "true"
        const attributeValue =
          response.data.attributeValue || response.data.value;
        const isDisabled = attributeValue === "true";
        return isDisabled;
      },
    }
  );

  const {
    data: dataTransfers,
    isError: isErrorTransfers,
    isFetching: isFetchingTransfers,
  } = useQuery(
    [
      "transfers",
      memberInfo?.memberId,
      transferType,
      dateRange,
      page,
      pageSize,
    ],
    () =>
      fetchTransferAll(
        memberInfo?.idCustomer,
        transferType,
        dateRange,
        page,
        pageSize
      ),
    {
      enabled: !!memberInfo?.memberId && !companyView,
      keepPreviousData: true, // Keep previous page data while loading
      //staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    }
  );

  const resendTransferLinkMutation = useMutation(
    async (idMoneyMovement) => {
      const response = await HttpService.getAxiosClient().post(
        window.API_URL +
          `/member/transfer/${idMoneyMovement}/resend?customerId=${memberInfo?.idCustomer}`
      );
      return response.data;
    },
    {
      onSuccess: () => {
        setFeedback({
          open: true,
          type: "success",
          message: Labels.getLabel(
            "memberDetail.resendLinkSuccess",
            "Transfer link resent successfully!"
          ),
        });
      },
      onError: (error) => {
        console.error("Failed to resend transfer:", error);
        setFeedback({
          open: true,
          type: "error",
          message: Labels.getLabel(
            "memberDetail.resendLinkError",
            "Failed to resend transfer link. Please try again."
          ),
        });
      },
    }
  );

  // Mutation for updating PAD enabled status
  const updatePadStatusMutation = useMutation(
    async (padEnabledStatus) => {
      const response = await HttpService.getAxiosClient().post(
        window.API_URL + "/pad/status",
        {
          memberId: memberInfo?.memberId,
          companyId: memberInfo?.companyId,
          padEnabled: padEnabledStatus,
        }
      );
      return response.data;
    },
    {
      onSuccess: () => {
        setFeedback({
          open: true,
          type: "success",
          message: Labels.getLabel(
            "memberDetail.padStatusUpdateSuccess",
            "PAD status updated successfully!"
          ),
        });
      },
      onError: (error) => {
        console.error("Failed to update PAD status:", error);
        // Revert the toggle state on error
        setPadEnabled(!padEnabled);
        setFeedback({
          open: true,
          type: "error",
          message: Labels.getLabel(
            "memberDetail.padStatusUpdateError",
            "Failed to update PAD status. Please try again."
          ),
        });
      },
    }
  );

  // Mutation for updating PAD enabled status
  const updateDDLimitValidationMutation = useMutation(
    async (ddLimitValidationStatus) => {
      const response = await HttpService.getAxiosClient().post(
        window.API_URL + "/pad/dd-limit",
        {
          memberId: memberInfo?.memberId,
          companyId: memberInfo?.companyId,
          ddLimitEnabled: ddLimitValidationStatus,
        }
      );
      return response.data;
    },
    {
      onSuccess: () => {
        setFeedback({
          open: true,
          type: "success",
          message: Labels.getLabel(
            "memberDetail.ddLimitValidationUpdateSuccess",
            "DD Limit Validation updated successfully!"
          ),
        });
      },
      onError: (error) => {
        console.error("Failed to update DD Limit Validation:", error);
        // Revert the toggle state on error
        setDdLimitValidation(!ddLimitValidation);
        setFeedback({
          open: true,
          type: "error",
          message: Labels.getLabel(
            "memberDetail.ddLimitValidationUpdateError",
            "Failed to update DD Limit Validation. Please try again."
          ),
        });
      },
    }
  );

  // Function to fetch customer activity data
  const fetchCustomerActivity = async (
    page = 0,
    pageSize = 50,
    filters = {}
  ) => {
    if (!memberInfo?.memberId || !memberInfo?.companyId) {
      throw new Error("No member ID or company ID available");
    }

    let url = `${window.API_URL}/member/activity?memberId=${memberInfo.memberId}&companyId=${memberInfo.companyId}&page=${page}&pageSize=${pageSize}`;

    // Add filter parameters to URL
    if (filters.method) {
      url += `&method=${encodeURIComponent(filters.method)}`;
    }
    if (filters.path) {
      url += `&path=${encodeURIComponent(filters.path)}`;
    }
    if (filters.responseCode) {
      url += `&responseCode=${encodeURIComponent(filters.responseCode)}`;
    }

    const response = await HttpService.getAxiosClient().get(url);
    return response.data || {};
  };

  // Handler for fetching customer activity (initial load)
  const handleGetCustomerActivity = async () => {
    try {
      // Set loading state and open dialog
      setActivityDialog((prev) => ({
        ...prev,
        loading: true,
        open: true,
        activities: [],
        title: "Customer Activity",
        context: `Member: ${memberInfo.memberId} - Company: ${memberInfo.companyId}`,
        page: 0,
        pageSize: 50,
      }));

      // Fetch first page
      const data = await fetchCustomerActivity(0, 50);

      // Update dialog with activities
      setActivityDialog((prev) => ({
        ...prev,
        loading: false,
        activities: data.activities || [],
        totalCount: data.totalCount || 0,
        page: 0,
        pageSize: 50,
        hasMore: data.hasMore || false,
      }));

      // Show feedback to user
      if (data.activities && data.activities.length > 0) {
      } else {
        console.log(
          `No activity records found for member ${memberInfo.memberId}`
        );
      }
    } catch (error) {
      console.error("Error fetching member activity:", error);
      setActivityDialog((prev) => ({
        ...prev,
        loading: false,
        activities: [],
        totalCount: 0,
        page: 0,
        pageSize: 50,
        hasMore: false,
      }));
      setFeedback({
        open: true,
        type: "error",
        message: "Error fetching member activity",
      });
    }
  };

  // Handler for pagination changes
  const handleActivityPageChange = async (newPage) => {
    try {
      setActivityDialog((prev) => ({ ...prev, fetchingData: true }));

      const data = await fetchCustomerActivity(
        newPage,
        activityDialog.pageSize,
        activityDialog.filters
      );

      setActivityDialog((prev) => ({
        ...prev,
        fetchingData: false,
        activities: data.activities || [],
        page: newPage,
        totalCount: data.totalCount || 0,
        hasMore: data.hasMore || false,
      }));
    } catch (error) {
      console.error("Error fetching activity page:", error);
      setActivityDialog((prev) => ({ ...prev, fetchingData: false }));
      setFeedback({
        open: true,
        type: "error",
        message: "Error fetching activity page",
      });
    }
  };

  // Handler for page size changes
  const handleActivityPageSizeChange = async (newPageSize) => {
    try {
      setActivityDialog((prev) => ({ ...prev, fetchingData: true }));

      const data = await fetchCustomerActivity(
        0,
        newPageSize,
        activityDialog.filters
      );

      setActivityDialog((prev) => ({
        ...prev,
        fetchingData: false,
        activities: data.activities || [],
        page: 0,
        pageSize: newPageSize,
        totalCount: data.totalCount || 0,
        hasMore: data.hasMore || false,
      }));
    } catch (error) {
      console.error("Error fetching activity with new page size:", error);
      setActivityDialog((prev) => ({ ...prev, fetchingData: false }));
      setFeedback({
        open: true,
        type: "error",
        message: "Error fetching activity with new page size",
      });
    }
  };

  // Handler for filter changes
  const handleActivityFilterChange = async (filterModel) => {
    console.log("📥 Received filter model in MemberDetail:", filterModel);

    // Convert Material-UI filter model to our API format
    const filters = {};

    if (filterModel?.items && filterModel.items.length > 0) {
      filterModel.items.forEach((filter) => {
        if (
          filter.value !== undefined &&
          filter.value !== null &&
          filter.value !== ""
        ) {
          // Use columnField instead of field
          const fieldName = filter.columnField || filter.field;
          filters[fieldName] = filter.value;
        }
      });
    }

    console.log("🎯 Converted filters for API:", filters);

    try {
      // Set fetchingData true to show spinner, but keep loading false so UI isn't blocked
      setActivityDialog((prev) => ({ ...prev, fetchingData: true }));

      const data = await fetchCustomerActivity(
        0,
        activityDialog.pageSize,
        filters
      );

      // Update data and turn off fetchingData
      setActivityDialog((prev) => ({
        ...prev,
        fetchingData: false,
        activities: data.activities || [],
        page: 0, // Reset to first page when filtering
        totalCount: data.totalCount || 0,
        hasMore: data.hasMore || false,
        filters: filters, // Save current filters
      }));
    } catch (error) {
      console.error("Error fetching activity with filters:", error);
      setActivityDialog((prev) => ({ ...prev, fetchingData: false }));
      setFeedback({
        open: true,
        type: "error",
        message: "Error applying filters",
      });
    }
  };

  // Handler for fetching beneficiary events
  const handleGetBeneficiaryEvents = async (beneficiary) => {
    try {
      if (!beneficiary) {
        console.error("No beneficiary data available");
        return;
      }

      // Set loading state
      const beneficiaryName = beneficiary.isOwn
        ? beneficiary.alias || "Own Account"
        : `${beneficiary.firstName || ""} ${
            beneficiary.lastName || ""
          }`.trim() || beneficiary.idCommunication;

      setEventsDialog((prev) => ({
        ...prev,
        loading: true,
        open: true,
        events: [],
        title: `${
          beneficiary.isOwn ? "Own Account" : "Beneficiary"
        } Card Validation Events`,
        context: `${beneficiaryName} - ${
          beneficiary.isOwn ? "Own Account" : beneficiary.idCommunication
        }`,
      }));

      let url;

      // Determine API parameters based on isOwn flag
      if (beneficiary.isOwn) {
        // For own account, use member and company parameters
        if (!memberInfo?.memberId || !memberInfo?.companyId) {
          console.error("No member ID or company ID available for own account");
          setEventsDialog({
            open: true,
            loading: false,
            events: [],
          });
          return;
        }
        url = `${window.API_URL}/member/events?memberId=${memberInfo.memberId}&companyId=${memberInfo.companyId}&status=CREATED&type=rds_card_validate`;
      } else {
        // For not own account, use email parameter (could be email or phone)
        const email = beneficiary.idCommunication;
        if (!email) {
          console.error("No email/phone available for external beneficiary");
          setEventsDialog({
            open: true,
            loading: false,
            events: [],
          });
          return;
        }
        url = `${window.API_URL}/member/events?email=${email}&status=CREATED&type=rds_card_validate`;
      }

      // Make the actual API call
      const response = await HttpService.getAxiosClient().get(url);

      // Filter events by eventName = "rds_card_validate"
      const allEvents = response.data || [];
      const filteredEvents = allEvents.filter(
        (event) => event.eventName === "rds_card_validate"
      );

      // Update dialog with events
      setEventsDialog({
        open: true,
        loading: false,
        events: filteredEvents,
        title: `${
          beneficiary.isOwn ? "Own Account" : "Beneficiary"
        } Card Validation Events`,
        context: `${beneficiaryName} - ${
          beneficiary.isOwn ? "Own Account" : beneficiary.idCommunication
        }`,
      });

      // Log results
      if (filteredEvents.length > 0) {
        console.log(
          `Found ${filteredEvents.length} rds_card_validate events for beneficiary`
        );
      } else {
        console.log(`No rds_card_validate events found for beneficiary`);
      }
    } catch (error) {
      console.error("Error fetching beneficiary events:", error);
      setEventsDialog({
        open: true,
        loading: false,
        events: [],
      });
      setFeedback({
        open: true,
        type: "error",
        message: "Error fetching beneficiary events",
      });
    }
  };

  useEffect(() => {
    const { data } = dataBasicInfo || {};
    setBasicInfoData(data);

    // Set PAD enabled state from backend data
    if (data && data.padEnabled !== undefined) {
      setPadEnabled(data.padEnabled);
    }

    if (data && data.ddLimitValidation !== undefined) {
      setDdLimitValidation(data.ddLimitValidation);
    }
  }, [dataBasicInfo]);

  // Reset page to 0 when filters change
  useEffect(() => {
    setPage(0);
  }, [transferType, dateRange]);

  // Pagination handlers
  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setPage(0); // Reset to first page when page size changes
  };

  const handleClose = (event, reason) => {
    if (reason === "backdropClick") return; //avoid closing the modal by clicking the backdrop
    // Reset all relevant state when closing
    setBasicInfoData({});
    setPadEnabled(true);
    setDdLimitValidation(true);
    setTransferType("instant");
    setPage(0);
    setPageSize(10);
    setFeedback({ open: false, type: "success", message: "" });
    setEventsDialog({
      open: false,
      events: [],
      loading: false,
      title: "Card Validation Events",
      context: "",
    });
    setActivityDialog({
      open: false,
      activities: [],
      loading: false,
      fetchingData: false,
      title: "Customer Activity",
      context: "",
      totalCount: 0,
      page: 0,
      pageSize: 50,
      hasMore: false,
      filters: {},
    });
    // Clear React Query cache for basicInfo
    queryClient.removeQueries(["basicInfo"]);
    props.setOpen(false);
  };

  const handleFeedbackClose = () => {
    setFeedback({ ...feedback, open: false });
  };

  // Debug log for PayMeNow rendering
  console.log(
    "🎯 Rendering MemberDetail - isPayMeNowEnabled:",
    isPayMeNowEnabled
  );
  console.log(memberInfo);

  return (
    <Dialog
      fullWidth={true}
      maxWidth="100%"
      open={props.open}
      onClose={props.handleClose}
    >
      <Box
        component="form"
        noValidate
        autoComplete="off"
        sx={{
          height: "90vh",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Merged header: title and close button */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            backgroundColor: "primary.main",
            px: 3,
            minHeight: 52,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="h6" component="div" color="white">
              {props.dialogTitle}
            </Typography>
            {isFetchingBasicInfo ? (
              <CircularProgress sx={{ color: "white" }} size={20} />
            ) : (
              <AccountCircleIcon sx={{ color: "white" }} />
            )}
          </Box>
          <Box>
            {/* Removed credit/debit buttons from here - now in CardView */}
            <IconButton
              edge="end"
              color="inherit"
              size="small"
              onClick={handleClose}
              aria-label="close"
            >
              <CloseIcon sx={{ color: "white" }} />
            </IconButton>
          </Box>
        </Box>

        <DialogContent
          sx={{
            height: "80%",
            flexShrink: 0,
            paddingTop: 5,
          }}
        >
          {isLoadingBasicInfo ? (
            <CircularProgress color="inherit" />
          ) : isErrorBasicInfo ? (
            <h4>
              {Labels.getLabel(
                "memberDetail.errorGettingInfo",
                "There was an error getting the info"
              )}
            </h4>
          ) : (
            <Grid
              style={{ marginLeft: "2px", marginRight: "2px" }}
              container
              spacing={1.5}
            >
              <Grid container spacing={1}>
                <Grid item xs={2.5}>
                  <Typography>
                    {Labels.getLabel("memberDetail.name", "Name:")}{" "}
                  </Typography>
                </Grid>
                <Grid item xs={3.5}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <BadgeIcon fontSize="small" />
                    <Typography>
                      {memberInfo?.name
                        ? memberInfo.name
                        : memberInfo?.firstName + " " + memberInfo?.lastName}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={2.5}>
                  <Typography>
                    {Labels.getLabel("memberDetail.email", "Email:")}
                  </Typography>
                </Grid>
                <Grid item xs={3.5}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <AlternateEmailIcon fontSize="small" />
                    <Typography>{memberInfo.email}</Typography>
                  </Box>
                </Grid>
                <Grid item xs={2.5}>
                  <Typography>
                    {Labels.getLabel("memberDetail.memberId", "Member Id:")}
                  </Typography>
                </Grid>
                <Grid item xs={3.5}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <FingerprintIcon fontSize="small" />
                    <Typography>{memberInfo.memberId}</Typography>
                  </Box>
                </Grid>
                <Grid item xs={2.5}>
                  <Typography>
                    {Labels.getLabel("memberDetail.company", "Company:")}
                  </Typography>
                </Grid>
                <Grid item xs={3.5}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <BusinessIcon fontSize="small" />
                    <Typography>
                      {(memberInfo?.companyName ? memberInfo.companyName : "") +
                        " (" +
                        memberInfo.companyId +
                        ")"}
                    </Typography>
                  </Box>
                </Grid>
                <RenderOnRole roles={["member-view-activity"]}>
                  <Grid item xs={2.5}>
                    <Typography>
                      {Labels.getLabel(
                        "memberDetail.customerActivity",
                        "Customer Activity:"
                      )}
                    </Typography>
                  </Grid>
                  <Grid item xs={9.5}>
                    <Typography>
                      <Tooltip
                        title={Labels.getLabel(
                          "memberDetail.viewCustomerActivity",
                          "View customer activity logs"
                        )}
                      >
                        <IconButton
                          onClick={handleGetCustomerActivity}
                          color="primary"
                          size="small"
                          sx={{
                            padding: 0,
                            marginRight: "5px",
                            verticalAlign: "middle",
                          }}
                        >
                          <TextSnippetIcon />
                        </IconButton>
                      </Tooltip>
                    </Typography>
                  </Grid>
                </RenderOnRole>
                {!isPadDisabled && (
                  <Box
                    sx={{
                      p: 0.5,
                      mt: 1,
                      mr: 2,
                      borderRadius: 1,
                      width: "100%",
                    }}
                  >
                    <Divider />
                    <Grid container spacing={0}>
                      <RenderOnRole roles={["pad-update-status"]}>
                        <Grid item xs={4}>
                          <Box sx={{ p: 0.5, mb: 1.5 }}>
                            <div
                              style={{
                                padding: "0px",
                                margin: "0",
                                display: "flex",
                                flexDirection: "row",
                              }}
                            >
                              <div
                                style={{
                                  flex: 1,
                                  display: "flex",
                                  flexDirection: "column",
                                  justifyContent: "center",
                                }}
                              >
                                <Typography>
                                  {Labels.getLabel(
                                    "memberDetail.padEnabled",
                                    "PAD Enabled:"
                                  )}
                                </Typography>
                              </div>
                              <div
                                style={{
                                  flex: 1,
                                  display: "flex",
                                  flexDirection: "column",
                                  justifyContent: "center",
                                  alignItems: "flex-start",
                                }}
                              >
                                <FormControlLabel
                                  control={
                                    <Switch
                                      checked={padEnabled}
                                      onChange={(event) => {
                                        const newValue = event.target.checked;
                                        setPadEnabled(newValue);
                                        updatePadStatusMutation.mutate(
                                          newValue
                                        );
                                      }}
                                      disabled={
                                        updatePadStatusMutation.isPending
                                      }
                                      color="primary"
                                    />
                                  }
                                  label={
                                    <Typography
                                      variant="body2"
                                      color={
                                        padEnabled
                                          ? "success.main"
                                          : "error.main"
                                      }
                                    >
                                      {padEnabled
                                        ? Labels.getLabel(
                                            "memberDetail.padOn",
                                            "On"
                                          )
                                        : Labels.getLabel(
                                            "memberDetail.padOff",
                                            "Off"
                                          )}
                                    </Typography>
                                  }
                                />
                              </div>
                            </div>
                          </Box>
                        </Grid>
                      </RenderOnRole>
                      <RenderOnRole roles={["pad-view-info"]}>
                        <Grid item xs={4}>
                          <Box sx={{ pr: 7, p: 0.5, mb: 1.5 }}>
                            <div
                              style={{
                                display: "flex",
                                flexDirection: "row",
                              }}
                            >
                              <div
                                style={{
                                  flex: 1,
                                  display: "flex",
                                  flexDirection: "column",
                                  justifyContent: "center",
                                }}
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 0.5,
                                  }}
                                >
                                  <MoneyOffIcon fontSize="small" />
                                  <Typography>
                                    {Labels.getLabel(
                                      "memberDetail.pendingPayback",
                                      "Pending Payback:"
                                    )}
                                  </Typography>
                                </Box>
                              </div>
                              <div
                                style={{
                                  flex: 1,
                                  display: "flex",
                                  flexDirection: "column",
                                  justifyContent: "center",
                                  alignItems: "flex-start",
                                }}
                              >
                                <Typography
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    fontFamily: "Menlo",
                                    fontSize: "14px",
                                    color: "red",
                                  }}
                                >
                                  {formatCurrency(
                                    basicInfoData && basicInfoData.pending
                                  )}
                                </Typography>
                              </div>
                            </div>
                          </Box>
                        </Grid>
                      </RenderOnRole>

                      <Grid item xs={4}>
                        <Box sx={{ pr: 7, p: 0.5, mb: 1.5 }}>
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "row",
                            }}
                          >
                            <div
                              style={{
                                flex: 1,
                                display: "flex",
                                flexDirection: "column",
                                justifyContent: "center",
                              }}
                            >
                              <RenderOnRole
                                roles={["pad-update-dd-limit-validation"]}
                              >
                                <Typography>
                                  {Labels.getLabel(
                                    "memberDetail.DDLimitValidation",
                                    "DD Limit Validation:"
                                  )}
                                </Typography>
                              </RenderOnRole>
                            </div>
                            <div
                              style={{
                                flex: 1,
                                display: "flex",
                                flexDirection: "column",
                                justifyContent: "center",
                                alignItems: "flex-start",
                              }}
                            >
                              <RenderOnRole
                                roles={["pad-update-dd-limit-validation"]}
                              >
                                <FormControlLabel
                                  control={
                                    <Switch
                                      checked={ddLimitValidation}
                                      onChange={(event) => {
                                        const newValue = event.target.checked;
                                        setDdLimitValidation(newValue);
                                        updateDDLimitValidationMutation.mutate(
                                          newValue
                                        );
                                      }}
                                      disabled={
                                        updateDDLimitValidationMutation.isPending
                                      }
                                      color="primary"
                                    />
                                  }
                                  label={
                                    <Typography
                                      variant="body2"
                                      color={
                                        ddLimitValidation
                                          ? "success.main"
                                          : "error.main"
                                      }
                                    >
                                      {ddLimitValidation
                                        ? Labels.getLabel(
                                            "memberDetail.ddLimitValidationOn",
                                            "On"
                                          )
                                        : Labels.getLabel(
                                            "memberDetail.ddLimitValidationOff",
                                            "Off"
                                          )}
                                    </Typography>
                                  }
                                />
                              </RenderOnRole>
                            </div>
                          </div>
                        </Box>
                      </Grid>

                      {/* Row 2 */}
                      <RenderOnRole roles={["pad-view-info"]}>
                        <Grid item xs={4}>
                          <Box sx={{ p: 0.5, mb: 1.5 }}>
                            <div
                              style={{
                                display: "flex",
                                flexDirection: "row",
                              }}
                            >
                              <div
                                style={{
                                  flex: 1,
                                  display: "flex",
                                  flexDirection: "column",
                                  justifyContent: "center",
                                }}
                              >
                                <Typography>
                                  {Labels.getLabel(
                                    "memberDetail.periodStartDate",
                                    "Period Start Date:"
                                  )}
                                </Typography>
                              </div>
                              <div
                                style={{
                                  flex: 1,
                                  display: "flex",
                                  flexDirection: "column",
                                  justifyContent: "center",
                                  alignItems: "flex-start",
                                }}
                              >
                                <Typography
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    fontFamily: "Menlo",
                                    fontSize: "14px",
                                  }}
                                >
                                  <CalendarMonthIcon
                                    style={{ paddingRight: "5px" }}
                                  />
                                  {basicInfoData &&
                                    basicInfoData.periodStartDate}
                                </Typography>
                              </div>
                            </div>
                          </Box>
                        </Grid>
                        <Grid item xs={4}>
                          <Box sx={{ pr: 7, p: 0.5, mb: 1.5 }}>
                            <div
                              style={{
                                display: "flex",
                                flexDirection: "row",
                              }}
                            >
                              <div
                                style={{
                                  flex: 1,
                                  display: "flex",
                                  flexDirection: "column",
                                  justifyContent: "center",
                                }}
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 0.5,
                                  }}
                                >
                                  <SavingsIcon fontSize="small" />
                                  <Typography>
                                    {Labels.getLabel(
                                      "memberDetail.padAccumulated",
                                      "PAD Accumulated:"
                                    )}
                                  </Typography>
                                </Box>
                              </div>
                              <div
                                style={{
                                  flex: 1,
                                  display: "flex",
                                  flexDirection: "column",
                                  justifyContent: "center",
                                  alignItems: "flex-start",
                                }}
                              >
                                <Typography
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    fontFamily: "Menlo",
                                    fontSize: "14px",
                                  }}
                                >
                                  {formatCurrency(
                                    basicInfoData && basicInfoData.padGenerated
                                  )}
                                </Typography>
                              </div>
                            </div>
                          </Box>
                        </Grid>
                      </RenderOnRole>

                      <Grid item xs={4}>
                        <Box sx={{ pr: 7, p: 0.5, mb: 1.5 }}>
                          <RenderOnRole roles={["pad-view-info-advanced"]}>
                            <div
                              style={{
                                display: "flex",
                                flexDirection: "row",
                              }}
                            >
                              <div
                                style={{
                                  flex: 1,
                                  display: "flex",
                                  flexDirection: "column",
                                  justifyContent: "center",
                                }}
                              >
                                <Typography>
                                  {Labels.getLabel(
                                    "memberDetail.DirectDepositAverage",
                                    "Direct Deposit Average:"
                                  )}
                                </Typography>
                              </div>
                              <div
                                style={{
                                  flex: 1,
                                  display: "flex",
                                  flexDirection: "column",
                                  justifyContent: "center",
                                  alignItems: "flex-start",
                                }}
                              >
                                <Typography
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    fontFamily: "Menlo",
                                    fontSize: "14px",
                                  }}
                                >
                                  {formatCurrency(
                                    basicInfoData &&
                                      basicInfoData.ddLimitAverage
                                  )}
                                </Typography>
                              </div>
                            </div>
                          </RenderOnRole>
                        </Box>
                      </Grid>

                      {/* Row 3 */}
                      <RenderOnRole roles={["pad-view-info"]}>
                        <Grid item xs={4}>
                          <Box sx={{ p: 0.5, mb: 1.5 }}>
                            <div
                              style={{
                                display: "flex",
                                flexDirection: "row",
                              }}
                            >
                              <div
                                style={{
                                  flex: 1,
                                  display: "flex",
                                  flexDirection: "column",
                                  justifyContent: "center",
                                }}
                              >
                                <Typography>
                                  {Labels.getLabel(
                                    "memberDetail.periodEndDate",
                                    "Period End Date:"
                                  )}
                                </Typography>
                              </div>
                              <div
                                style={{
                                  flex: 1,
                                  display: "flex",
                                  flexDirection: "column",
                                  justifyContent: "center",
                                  alignItems: "flex-start",
                                }}
                              >
                                <Typography
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    fontFamily: "Menlo",
                                    fontSize: "14px",
                                  }}
                                >
                                  <CalendarMonthIcon
                                    style={{ paddingRight: "5px" }}
                                  />
                                  {basicInfoData && basicInfoData.periodEndDate}
                                </Typography>
                              </div>
                            </div>
                          </Box>
                        </Grid>
                      </RenderOnRole>
                      <RenderOnRole roles={["pad-view-info"]}>
                        <Grid item xs={4}>
                          <Box sx={{ pr: 7, p: 0.5, mb: 1.5 }}>
                            <div
                              style={{
                                display: "flex",
                                flexDirection: "row",
                              }}
                            >
                              <div
                                style={{
                                  flex: 1,
                                  display: "flex",
                                  flexDirection: "column",
                                  justifyContent: "center",
                                }}
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 0.5,
                                  }}
                                >
                                  <DownloadDoneIcon fontSize="small" />
                                  <Typography>
                                    {Labels.getLabel(
                                      "memberDetail.padTaken",
                                      "PAD Taken:"
                                    )}
                                  </Typography>
                                </Box>
                              </div>
                              <div
                                style={{
                                  flex: 1,
                                  display: "flex",
                                  flexDirection: "column",
                                  justifyContent: "center",
                                  alignItems: "flex-start",
                                }}
                              >
                                <Typography
                                  sx={{
                                    fontFamily:
                                      "Fira Mono, Menlo, Consolas, monospace",
                                    fontSize: "14px",
                                  }}
                                >
                                  {formatCurrency(
                                    basicInfoData && basicInfoData.padTaken
                                  )}
                                </Typography>
                              </div>
                            </div>
                          </Box>
                        </Grid>
                      </RenderOnRole>

                      <Grid item xs={4}>
                        <Box sx={{ pr: 7, p: 0.5, mb: 1.5 }}>
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "row",
                            }}
                          >
                            <RenderOnRole roles={["pad-view-info-advanced"]}>
                              <div
                                style={{
                                  flex: 1,
                                  display: "flex",
                                  flexDirection: "column",
                                  justifyContent: "center",
                                }}
                              >
                                <Typography>
                                  {Labels.getLabel(
                                    "memberDetail.BaseLimit",
                                    "Base Limit:"
                                  )}
                                </Typography>
                              </div>
                              <div
                                style={{
                                  flex: 1,
                                  display: "flex",
                                  flexDirection: "column",
                                  justifyContent: "center",
                                  alignItems: "flex-start",
                                }}
                              >
                                <Typography
                                  sx={{
                                    fontFamily:
                                      "Fira Mono, Menlo, Consolas, monospace",
                                    fontSize: "14px",
                                  }}
                                >
                                  {formatCurrency(
                                    basicInfoData && basicInfoData.ddLimitBase
                                  )}
                                </Typography>
                              </div>
                            </RenderOnRole>
                          </div>
                        </Box>
                      </Grid>

                      {/* Row 4 */}
                      <Grid item xs={4}>
                        {/* Empty cell for alignment */}
                      </Grid>
                      <RenderOnRole roles={["pad-view-info"]}>
                        <Grid item xs={4}>
                          <Box sx={{ pr: 7, p: 0.5 }}>
                            <div
                              style={{
                                display: "flex",
                                flexDirection: "row",
                              }}
                            >
                              <div
                                style={{
                                  flex: 1,
                                  display: "flex",
                                  flexDirection: "column",
                                  justifyContent: "center",
                                }}
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 0.5,
                                  }}
                                >
                                  <PaidIcon fontSize="small" />
                                  <Typography>
                                    {Labels.getLabel(
                                      "memberDetail.padBalance",
                                      "PAD Balance:"
                                    )}
                                  </Typography>
                                </Box>
                              </div>
                              <div
                                style={{
                                  flex: 1,
                                  display: "flex",
                                  flexDirection: "column",
                                  justifyContent: "center",
                                  alignItems: "flex-start",
                                }}
                              >
                                <Typography
                                  sx={{
                                    fontFamily:
                                      "Fira Mono, Menlo, Consolas, monospace",
                                    fontSize: "14px",
                                  }}
                                >
                                  {formatCurrency(
                                    basicInfoData && basicInfoData.balance
                                  )}
                                </Typography>
                              </div>
                            </div>
                          </Box>
                        </Grid>
                      </RenderOnRole>
                      <Grid item xs={4}>
                        {/* Empty cell for alignment */}
                      </Grid>
                    </Grid>
                  </Box>
                )}
              </Grid>
              <br />
              <br />
              <Divider style={{ margin: "15px" }} />
            </Grid>
          )}
          {isPayMeNowEnabled && (
            <>
              <RenderOnRole roles={["paymenow-view-info"]}>
                <Accordion
                  expanded={payMeNowExpanded}
                  onChange={(event, isExpanded) =>
                    setPayMeNowExpanded(isExpanded)
                  }
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls="payme-now-content"
                    id="payme-now-header"
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography sx={{ width: "100%", flexShrink: 0 }}>
                        {Labels.getLabel("memberDetail.payMeNow", "PayMeNow")}
                      </Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <PayMeNowList
                      isLoading={isFetchingPayMeNow}
                      isError={isErrorPayMeNow}
                      data={dataPayMeNow}
                    />
                  </AccordionDetails>
                </Accordion>
                <br />
              </RenderOnRole>
            </>
          )}
          {!isPadDisabled && (
            <>
              <RenderOnRole roles={["pad-view-info"]}>
                <Accordion>
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls="panel1a-content"
                    id="panel1a-header"
                  >
                    <Typography sx={{ width: "100%", flexShrink: 0 }}>
                      {Labels.getLabel(
                        "memberDetail.padAdvances",
                        "PayAnyDay Advances"
                      )}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <PayAnyDayAdvances
                      isLoading={isFetchingAdvancesDetail}
                      isError={isErrorAdvancesDetail}
                      data={dataAdvancesDetail}
                    />
                  </AccordionDetails>
                </Accordion>
              </RenderOnRole>
              <br />
              {!companyView && (
                <Fragment>
                  {" "}
                  <RenderOnRole roles={["pad-view-info-advanced"]}>
                    <Accordion>
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        aria-controls="panel1a-content"
                        id="panel1a-header"
                      >
                        <Typography sx={{ width: "100%", flexShrink: 0 }}>
                          {Labels.getLabel(
                            "memberDetail.padPayback",
                            "PayAnyDay Payback"
                          )}
                        </Typography>
                      </AccordionSummary>
                      <PayAnyDayPaybacks
                        isLoading={isFetchingPaybackDetail}
                        isError={isErrorPaybackDetail}
                        data={dataPaybackDetail}
                      />
                    </Accordion>
                    <br />
                  </RenderOnRole>
                </Fragment>
              )}
              <RenderOnRole roles={["pad-view-info"]}>
                <Accordion>
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls="panel1a-content"
                    id="panel1a-header"
                  >
                    <Typography sx={{ width: "100%", flexShrink: 0 }}>
                      {Labels.getLabel(
                        "memberDetail.timeAttendance",
                        "Time And Attendance"
                      )}
                    </Typography>
                  </AccordionSummary>
                  <QueryTimeAttendance
                    hidePeriods={true}
                    hideTitle={true}
                    filterString={memberInfo.email}
                  />
                </Accordion>
                <br />
              </RenderOnRole>
            </>
          )}
          {!companyView && (
            <>
              <RenderOnRole roles={["card-credits-view-info"]}>
                <Accordion>
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls="panel1a-content"
                    id="panel1a-header"
                  >
                    <Typography sx={{ width: "100%", flexShrink: 0 }}>
                      {Labels.getLabel(
                        "memberDetail.cardCredits",
                        "Card Credits (DD/Loads)"
                      )}
                    </Typography>
                  </AccordionSummary>
                  <CardCredits
                    isLoading={isFetchingCardCredits}
                    isError={isErrorCardCredits}
                    data={dataCardCredits}
                  />
                </Accordion>
                <br />
              </RenderOnRole>
            </>
          )}
          {!companyView && (
            <>
              <RenderOnRole roles={["money-transfers-view-info"]}>
                <Accordion>
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls="panel1a-content"
                    id="panel1a-header"
                  >
                    <Typography sx={{ width: "100%", flexShrink: 0 }}>
                      {Labels.getLabel(
                        "memberDetail.sendMoneyTransfers",
                        "Send Money Transfers"
                      )}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box
                      sx={{
                        mb: 2,
                        display: "flex",
                        gap: 2,
                        alignItems: "center",
                      }}
                    >
                      <FormControl size="small" sx={{ minWidth: 200 }}>
                        <InputLabel id="transfer-type-label">
                          {Labels.getLabel(
                            "memberDetail.transferType",
                            "Transfer Type"
                          )}
                        </InputLabel>
                        <Select
                          labelId="transfer-type-label"
                          id="transfer-type-select"
                          value={transferType}
                          label={Labels.getLabel(
                            "memberDetail.transferType",
                            "Transfer Type"
                          )}
                          onChange={(e) => setTransferType(e.target.value)}
                        >
                          <MenuItem value="instant">
                            {Labels.getLabel("memberDetail.instant", "Instant")}
                          </MenuItem>
                          <MenuItem value="nextDay">
                            {Labels.getLabel(
                              "memberDetail.nextDay",
                              "Next Day"
                            )}
                          </MenuItem>
                          <MenuItem value="scheduled">
                            {Labels.getLabel(
                              "memberDetail.scheduled",
                              "Scheduled"
                            )}
                          </MenuItem>
                        </Select>
                      </FormControl>
                    </Box>
                    <SendMoneyTransferList
                      isLoading={isFetchingTransfers}
                      isError={isErrorTransfers}
                      data={dataTransfers}
                      onResendTransfer={(idMoneyMovement) =>
                        resendTransferLinkMutation.mutate(idMoneyMovement)
                      }
                      isResending={resendTransferLinkMutation.isPending}
                      resendingId={resendTransferLinkMutation.variables}
                      transferType={transferType}
                      page={page}
                      pageSize={pageSize}
                      onPageChange={handlePageChange}
                      onPageSizeChange={handlePageSizeChange}
                      enablePagination={transferType === "instant"}
                      onGetBeneficiaryEvents={handleGetBeneficiaryEvents}
                    />
                  </AccordionDetails>
                </Accordion>
                <br />
              </RenderOnRole>
            </>
          )}

          {/* ── Support Chat Conversations ── */}
          <RenderOnRole roles={["chat-support"]}>
            <Accordion
              expanded={chatsExpanded}
              onChange={(_, isExpanded) => setChatsExpanded(isExpanded)}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="member-chats-content"
                id="member-chats-header"
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <ForumIcon fontSize="small" sx={{ color: "#0d7f81" }} />
                  <Typography sx={{ flexShrink: 0 }}>
                    Support Chat Conversations
                  </Typography>
                  {isFetchingConversations && (
                    <CircularProgress size={14} sx={{ ml: 1 }} />
                  )}
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 0 }}>
                {isErrorConversations ? (
                  <Alert severity="error" sx={{ m: 2 }}>
                    Failed to load conversations.
                  </Alert>
                ) : (
                  <Paper variant="outlined" sx={{ m: 1 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: "#0d7f81" }}>
                          {[
                            "Conversation ID",
                            "Status",
                            "Assigned Agent",
                            "Created",
                            "Last Updated",
                          ].map((h) => (
                            <TableCell
                              key={h}
                              sx={{
                                color: "white",
                                fontWeight: "bold",
                                fontSize: 12,
                              }}
                            >
                              {h}
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {!memberConversations ||
                        memberConversations.length === 0 ? (
                          <TableRow>
                            <TableCell
                              colSpan={5}
                              align="center"
                              sx={{ py: 3, color: "text.secondary" }}
                            >
                              {isFetchingConversations
                                ? "Loading…"
                                : "No conversations found for this member."}
                            </TableCell>
                          </TableRow>
                        ) : (
                          memberConversations.map((conv) => (
                            <TableRow
                              key={conv.id}
                              hover
                              sx={{ cursor: "pointer" }}
                              onClick={() => setChatConvId(conv.id)}
                            >
                              <TableCell
                                sx={{ fontFamily: "monospace", fontSize: 11 }}
                              >
                                {conv.id}
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={
                                    conv.status === "BOT"
                                      ? "Assistant"
                                      : conv.status === "HUMAN"
                                        ? "Agent"
                                        : conv.status
                                  }
                                  color={
                                    {
                                      ESCALATED: "warning",
                                      HUMAN: "success",
                                      CLOSED: "default",
                                      BOT: "info",
                                    }[conv.status] || "default"
                                  }
                                  size="small"
                                />
                              </TableCell>
                              <TableCell>
                                {conv.assignedAgentId || (
                                  <em style={{ color: "#999" }}>Unassigned</em>
                                )}
                              </TableCell>
                              <TableCell sx={{ fontSize: 12 }}>
                                {conv.createdAt
                                  ? new Date(conv.createdAt).toLocaleString()
                                  : "—"}
                              </TableCell>
                              <TableCell sx={{ fontSize: 12 }}>
                                {conv.updatedAt
                                  ? new Date(conv.updatedAt).toLocaleString()
                                  : "—"}
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </Paper>
                )}
              </AccordionDetails>
            </Accordion>
            <br />
          </RenderOnRole>
        </DialogContent>
      </Box>

      {/* ── Inline chat conversation dialog ── */}
      <Dialog
        open={!!chatConvId}
        onClose={() => setChatConvId(null)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { height: "85vh" } }}
      >
        {chatConvId && (
          <SupportChatConversation
            conversationId={chatConvId}
            onClose={() => setChatConvId(null)}
          />
        )}
      </Dialog>
      <Snackbar
        open={feedback.open}
        autoHideDuration={6000}
        onClose={handleFeedbackClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleFeedbackClose}
          severity={feedback.type}
          sx={{ width: "100%" }}
        >
          {feedback.message}
        </Alert>
      </Snackbar>

      {/* Events Dialog */}
      <Dialog
        open={eventsDialog.open}
        onClose={() => setEventsDialog((prev) => ({ ...prev, open: false }))}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          Card Validation Events
          {eventsDialog.events.length > 0 && (
            <Typography variant="body2" color="textSecondary">
              Found {eventsDialog.events.length} rds_card_validate events
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ height: 400, width: "100%" }}>
            <DataGrid
              rows={eventsDialog.events.map((event, index) => ({
                id: index,
                ...event,
              }))}
              columns={[
                {
                  field: "cardInfo",
                  headerName: "Card Information",
                  width: 300,
                  renderCell: (params) => {
                    const alias = params.row.extraInfo1 || "";
                    const cardNumber = params.row.extraInfo2 || "";
                    const description = params.row.description || "";

                    // Determine chip color and variant based on description
                    const getStatusChip = (desc) => {
                      const status = desc.toUpperCase();
                      let color = "default";
                      let variant = "outlined";

                      switch (status) {
                        case "ACTIVE":
                          color = "success";
                          variant = "filled";
                          break;
                        case "APPROVED":
                          color = "success";
                          variant = "filled";
                          break;
                        case "UNPROVED":
                          color = "warning";
                          variant = "filled";
                          break;
                        case "EXPIRED":
                          color = "error";
                          variant = "filled";
                          break;
                        default:
                          color = "default";
                          variant = "outlined";
                      }

                      return (
                        <Chip
                          label={status || "UNKNOWN"}
                          color={color}
                          variant={variant}
                          size="small"
                          sx={{ fontSize: "0.65rem", height: "18px", mb: 0.5 }}
                        />
                      );
                    };

                    return (
                      <div style={{ position: "relative", width: "100%" }}>
                        {alias && (
                          <Typography
                            sx={{ fontSize: 12, fontWeight: 500, mb: 0 }}
                          >
                            {alias}
                          </Typography>
                        )}
                        <div style={{ position: "relative" }}>
                          {cardNumber ? (
                            <Typography
                              sx={{
                                fontSize: 11,
                                color: "text.secondary",
                                mb: 0,
                                paddingRight: description ? "80px" : "0px", // Reserve space for chip
                              }}
                            >
                              {cardNumber}
                            </Typography>
                          ) : (
                            <Typography
                              sx={{
                                fontSize: 12,
                                mb: 0,
                                paddingRight: description ? "80px" : "0px", // Reserve space for chip
                              }}
                            >
                              -
                            </Typography>
                          )}
                          {description && (
                            <div
                              style={{
                                position: "absolute",
                                right: 0,
                                top: 0,
                                display: "flex",
                                alignItems: "center",
                                height: "100%",
                              }}
                            >
                              {getStatusChip(description)}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  },
                },
                {
                  field: "eventDate",
                  headerName: "Created Date",
                  width: 160,
                  renderCell: (params) => {
                    const dateValue = params.value
                      ? new Date(params.value).toLocaleString()
                      : "-";

                    return (
                      <Typography sx={{ fontSize: "0.75rem" }}>
                        {dateValue}
                      </Typography>
                    );
                  },
                },
                {
                  field: "operationResult",
                  headerName: "Operation Result",
                  width: 150,
                  renderCell: (params) => {
                    const metaData = params.row.metaDataMap;
                    if (metaData && metaData.extraInfo06) {
                      const extraInfo06 = metaData.extraInfo06.toString();
                      const operationResult = extraInfo06.substring(0, 2);
                      const status =
                        operationResult === "00" ? "Success" : "Failed";
                      const color =
                        operationResult === "00" ? "success" : "error";

                      return (
                        <Typography
                          sx={{ fontSize: "0.75rem", color: `${color}.main` }}
                        >
                          {status}
                        </Typography>
                      );
                    }
                    return (
                      <Typography sx={{ fontSize: "0.75rem" }}>-</Typography>
                    );
                  },
                },
                {
                  field: "securityCodeValidationResult",
                  headerName: "CVV Validation",
                  width: 130,
                  renderCell: (params) => {
                    const metaData = params.row.metaDataMap;
                    if (metaData && metaData.extraInfo06) {
                      const extraInfo06 = metaData.extraInfo06.toString();
                      const securityCodeResult = extraInfo06.substring(2, 4);
                      const status =
                        securityCodeResult === "00" ? "Success" : "Failed";
                      const color =
                        securityCodeResult === "00" ? "success" : "error";

                      return (
                        <Typography
                          sx={{ fontSize: "0.75rem", color: `${color}.main` }}
                        >
                          {status}
                        </Typography>
                      );
                    }
                    return (
                      <Typography sx={{ fontSize: "0.75rem" }}>-</Typography>
                    );
                  },
                },
                {
                  field: "addressValidationResult",
                  headerName: "Address Validation",
                  width: 150,
                  renderCell: (params) => {
                    const metaData = params.row.metaDataMap;
                    if (metaData && metaData.extraInfo06) {
                      const extraInfo06 = metaData.extraInfo06.toString();
                      const addressResult = extraInfo06.substring(4, 6);
                      const status =
                        addressResult === "00" ? "Success" : "Failed";
                      const color =
                        addressResult === "00" ? "success" : "error";

                      return (
                        <Typography
                          sx={{ fontSize: "0.75rem", color: `${color}.main` }}
                        >
                          {status}
                        </Typography>
                      );
                    }
                    return (
                      <Typography sx={{ fontSize: "0.75rem" }}>-</Typography>
                    );
                  },
                },
                {
                  field: "metaDataMap",
                  headerName: "Event Data",
                  width: 350,
                  renderCell: (params) => {
                    let displayValue = "-";
                    if (params.value && typeof params.value === "object") {
                      displayValue = JSON.stringify(params.value.extraInfo06);
                    } else if (params.value) {
                      displayValue = params.value;
                    }

                    return (
                      <Typography sx={{ fontSize: "0.75rem" }}>
                        {displayValue}
                      </Typography>
                    );
                  },
                },
              ]}
              loading={eventsDialog.loading}
              autoHeight
              disableSelectionOnClick
              density="compact"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() =>
              setEventsDialog((prev) => ({ ...prev, open: false }))
            }
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Activity Dialog */}
      <CustomerActivityDialog
        open={activityDialog.open}
        onClose={() => setActivityDialog((prev) => ({ ...prev, open: false }))}
        activityData={{
          activities: activityDialog.activities,
          totalCount: activityDialog.totalCount,
          pageSize: activityDialog.pageSize,
          page: activityDialog.page,
        }}
        loading={activityDialog.loading}
        fetchingData={activityDialog.fetchingData}
        customerName={
          memberInfo?.firstName && memberInfo?.lastName
            ? `${memberInfo.firstName} ${memberInfo.lastName}`
            : memberInfo?.email || "Unknown"
        }
        customerEmail={memberInfo?.email || ""}
        onPageChange={handleActivityPageChange}
        onPageSizeChange={handleActivityPageSizeChange}
        onFilterChange={handleActivityFilterChange}
      />
    </Dialog>
  );
}
